import {
  FAIRE_DIRECT_CREDIT_FAQ,
  FAIRE_DIRECT_MARKETPLACE_INCENTIVE_MODAL,
  IDENTITY_VERIFICATION_MODAL_BRAND_NAME_QUERY_PARAM,
  IDENTITY_VERIFICATION_MODAL_LINK_TO_QUERY_PARAM,
  IDENTITY_VERIFICATION_SHOW_MODAL_QUERY_PARAM_VALUE,
  PROFILE_PICTURE_UPLOAD_MODAL_QUERY_PARAM,
  REQUEST_UNLISTED_PRODUCTS_MODAL,
  REQUESTED_BRAND_UNLISTED_PRODUCTS_BRAND_TOKEN_QUERY_PARAM,
  SAMPLES_REQUEST_MODAL,
  SAMPLES_REQUEST_MODAL_BRAND_TOKEN_QUERY_PARAM,
  SAMPLES_REQUEST_MODAL_SOURCE_QUERY_PARAM,
  SHOPIFY_PLAN_UPSELL_MODAL,
  SHOW_MODAL_QUERY_PARAM,
  TEAM_MEMBERS_MODAL_QUERY_PARAM,
} from "@faire/retailer-visitor-shared/components/App/AppModals/consts";
import { SamplesRequestModalSource } from "@faire/retailer-visitor-shared/components/Modals/SamplesRequestModal/types";
import { UnlistedProductsEntryPoint } from "@faire/retailer-visitor-shared/hooks/unlistedProducts/utils";
import { formatMoney, RoundingStrategy } from "@faire/web/common/money";
import { getCurrentPath } from "@faire/web/common/Path";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { trackFaireDirectCreditFaqModalView } from "@faire/web-api--source/events/faireDirect/view/creditFaqModal";
import { trackOpenWithFaireShopifyOfferBenefitsUpsellsToastToggleClick } from "@faire/web-api--source/events/openWithFaire/click/shopifyOfferBenefitsUpsellsToastToggle";
import { trackUnlistedProductsRequestCtaClick } from "@faire/web-api--source/events/unlistedProducts/click/requestCta";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { History } from "history";
import { useHistory } from "react-router-dom";

export const openFDCreditFAQModal = (
  brandName: string,
  history: History,
  minAmount?: IFaireMoney
) => {
  const showModal = getCurrentPath().searchParams.get("show_modal");
  if (showModal === FAIRE_DIRECT_CREDIT_FAQ) {
    return;
  }

  const path = getCurrentPath()
    .replace({
      show_modal: FAIRE_DIRECT_CREDIT_FAQ,
      brandName,
      orderMinAmount: formatMoney({
        money: minAmount ?? IFaireMoney.build({ amount_cents: 0 }),
        roundingStrategy: RoundingStrategy.DROP_CENTS_ROUND_UP,
      }),
    })
    .toString();
  trackFaireDirectCreditFaqModalView();
  history.push(path);
};

export const useOpenShopifyPlanUpsellModal = () => {
  const history = useHistory();
  const { isMobile } = useViewport();

  const openShopifyPlanUpsellModal = () => {
    trackOpenWithFaireShopifyOfferBenefitsUpsellsToastToggleClick("open");
    const showModal = getCurrentPath().searchParams.get("show_modal");
    if (showModal === SHOPIFY_PLAN_UPSELL_MODAL || !isMobile) {
      return;
    }
    const path = getCurrentPath()
      .replace({
        show_modal: SHOPIFY_PLAN_UPSELL_MODAL,
      })
      .toString();
    history.push(path);
  };

  return { openShopifyPlanUpsellModal };
};

export const useOpenMarketplaceIncentiveTermsModal = () => {
  const history = useHistory();

  const openMarketplaceIncentiveTermsModal = () => {
    const showModal = getCurrentPath().searchParams.get("show_modal");

    if (showModal === FAIRE_DIRECT_MARKETPLACE_INCENTIVE_MODAL) {
      return;
    }

    const show_modal = FAIRE_DIRECT_MARKETPLACE_INCENTIVE_MODAL;

    const path = getCurrentPath().replace({ show_modal }).toString();
    history.push(path);
  };

  return { openMarketplaceIncentiveTermsModal };
};

/**
 * Creates an url for opening the {@link IdentityVerificationModal}.
 *
 * @param source "preorders" for evergreen preorder IDV flow, or "ffw" for a
 *     specific flow during fashion events. Passing in "ffw" will be verified
 *     downstream against FFW being live, and falling back to "preorder" if
 *     that's not the case.
 * @param linkToHomepageInsteadOfCurrentPage if true, the user will be returned
 *     to the homepage after completing the IDV flow. Otherwise, the user will be
 *     returned to the page it originated from.
 * @param brandName Name of the brand, used only if the modal was shown on the
 *     brand page.
 */
export const getIdentityVerificationModalUrl = (
  linkToHomepageInsteadOfCurrentPage: boolean | undefined,
  brandName: string | undefined
): string => {
  const path = getCurrentPath().append({
    [SHOW_MODAL_QUERY_PARAM]:
      IDENTITY_VERIFICATION_SHOW_MODAL_QUERY_PARAM_VALUE,
  });

  if (!linkToHomepageInsteadOfCurrentPage) {
    path.append({
      [IDENTITY_VERIFICATION_MODAL_LINK_TO_QUERY_PARAM]:
        getCurrentPath().toString(),
    });
  }
  if (brandName) {
    path.append({
      [IDENTITY_VERIFICATION_MODAL_BRAND_NAME_QUERY_PARAM]: brandName,
    });
  }

  return path.toString();
};

export const openSamplesRequestModal = (
  history: History,
  brandToken: string | undefined,
  sourcePage?: SamplesRequestModalSource
): void => {
  const showModal = getCurrentPath().searchParams.get("show_modal");
  if (brandToken === undefined || showModal === SAMPLES_REQUEST_MODAL) {
    return;
  }
  const path = getCurrentPath()
    .replace({
      show_modal: SAMPLES_REQUEST_MODAL,
      [SAMPLES_REQUEST_MODAL_BRAND_TOKEN_QUERY_PARAM]: brandToken,
      [SAMPLES_REQUEST_MODAL_SOURCE_QUERY_PARAM]: sourcePage ?? "",
    })
    .toString();
  history.push(path);
};

export const openRequestUnlistedProductsModal = (
  history: History,
  brandToken?: string,
  entryPoint?: UnlistedProductsEntryPoint
) => {
  const showModal = getCurrentPath().searchParams.get("show_modal");
  if (showModal === REQUEST_UNLISTED_PRODUCTS_MODAL || !brandToken) {
    return;
  }
  const path = getCurrentPath()
    .replace({
      show_modal: REQUEST_UNLISTED_PRODUCTS_MODAL,
      [REQUESTED_BRAND_UNLISTED_PRODUCTS_BRAND_TOKEN_QUERY_PARAM]: brandToken,
    })
    .toString();
  trackUnlistedProductsRequestCtaClick(entryPoint ?? "");
  history.push(path);
};

export const openTeamMembersModal = (history: History) => {
  const showModal = getCurrentPath().searchParams.get("show_modal");
  if (showModal === TEAM_MEMBERS_MODAL_QUERY_PARAM) {
    return;
  }
  const path = getCurrentPath()
    .replace({
      show_modal: TEAM_MEMBERS_MODAL_QUERY_PARAM,
    })
    .toString();
  history.push(path);
};

export const openProfilePictureUploadModal = (history: History) => {
  const showModal = getCurrentPath().searchParams.get("show_modal");
  if (showModal === PROFILE_PICTURE_UPLOAD_MODAL_QUERY_PARAM) {
    return;
  }
  const path = getCurrentPath()
    .replace({
      show_modal: PROFILE_PICTURE_UPLOAD_MODAL_QUERY_PARAM,
    })
    .toString();
  history.push(path);
};
